import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useAuth } from '@/hooks/useAuth';

export interface MenuTutorialVideo {
  id: string;
  brand_id: string;
  menu_item_id: string;
  sidebar_type: 'main' | 'settings';
  lesson_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  lesson?: {
    id: string;
    title: string;
    content: any;
    content_type: string;
  };
}

export function useMenuTutorialVideos() {
  const { currentBrandId } = useBrandManager();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch tutorials
  const { data: tutorials = [], isLoading } = useQuery({
    queryKey: ['menu-tutorial-videos', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      
      const { data, error } = await supabase
        .from('os_menu_tutorial_videos')
        .select(`
          *,
          lesson:os_lms_lessons(id, title, content, content_type)
        `)
        .eq('brand_id', currentBrandId)
        .eq('is_active', true);

      if (error) throw error;
      return data as MenuTutorialVideo[];
    },
    enabled: !!currentBrandId,
  });

  // Fetch watched tutorials for current user
  const { data: watchedTutorials = [] } = useQuery({
    queryKey: ['menu-tutorial-watched', currentBrandId, user?.id],
    queryFn: async () => {
      if (!currentBrandId || !user?.id) return [];
      
      const { data, error } = await supabase
        .from('os_menu_tutorial_watched')
        .select('menu_item_id, sidebar_type')
        .eq('brand_id', currentBrandId)
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!currentBrandId && !!user?.id,
  });

  // Fetch global toggle setting from brand_settings
  const { data: showTutorialIcons = true } = useQuery({
    queryKey: ['show-tutorial-icons', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return true;
      
      const { data, error } = await supabase
        .from('brand_settings')
        .select('show_tutorial_icons')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (error) return true; // Default to true if not found
      return data?.show_tutorial_icons ?? true;
    },
    enabled: !!currentBrandId,
  });

  // Create maps for quick lookup
  const mainNavVideos: Record<string, { videoUrl: string; title: string; lessonId?: string }> = {};
  const settingsVideos: Record<string, { videoUrl: string; title: string; lessonId?: string }> = {};

  tutorials.forEach(tutorial => {
    const videoUrl = tutorial.lesson?.content?.video_url;
    if (videoUrl) {
      const entry = {
        videoUrl,
        title: tutorial.lesson?.title || '',
        lessonId: tutorial.lesson?.id,
      };
      if (tutorial.sidebar_type === 'main') {
        mainNavVideos[tutorial.menu_item_id] = entry;
      } else {
        settingsVideos[tutorial.menu_item_id] = entry;
      }
    }
  });

  // Create set of watched tutorial keys for quick lookup
  const watchedSet = new Set(
    watchedTutorials.map(w => `${w.sidebar_type}:${w.menu_item_id}`)
  );

  const isWatched = (menuItemId: string, sidebarType: 'main' | 'settings'): boolean => {
    return watchedSet.has(`${sidebarType}:${menuItemId}`);
  };

  // Mark tutorial as watched
  const markAsWatched = useMutation({
    mutationFn: async (data: { menuItemId: string; sidebarType: 'main' | 'settings' }) => {
      if (!currentBrandId || !user?.id) throw new Error('Missing brand or user');
      
      const { error } = await supabase
        .from('os_menu_tutorial_watched')
        .upsert({
          user_id: user.id,
          brand_id: currentBrandId,
          menu_item_id: data.menuItemId,
          sidebar_type: data.sidebarType,
        }, {
          onConflict: 'user_id,brand_id,menu_item_id,sidebar_type',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-watched', currentBrandId, user?.id] });
    },
  });

  // Clear all watched tutorials for current user
  const clearWatchedTutorials = useMutation({
    mutationFn: async () => {
      if (!currentBrandId || !user?.id) throw new Error('Missing brand or user');
      
      const { error, count } = await supabase
        .from('os_menu_tutorial_watched')
        .delete()
        .eq('brand_id', currentBrandId)
        .eq('user_id', user.id);

      if (error) throw error;
      return count;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-watched', currentBrandId, user?.id] });
    },
  });

  const upsertTutorial = useMutation({
    mutationFn: async (data: {
      menu_item_id: string;
      sidebar_type: 'main' | 'settings';
      lesson_id: string;
      is_active: boolean;
    }) => {
      if (!currentBrandId) throw new Error('No brand selected');
      
      const { error } = await supabase
        .from('os_menu_tutorial_videos')
        .upsert({
          brand_id: currentBrandId,
          menu_item_id: data.menu_item_id,
          sidebar_type: data.sidebar_type,
          lesson_id: data.lesson_id,
          is_active: data.is_active,
        }, {
          onConflict: 'brand_id,menu_item_id,sidebar_type',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos', currentBrandId] });
    },
  });

  const deleteTutorial = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_menu_tutorial_videos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos', currentBrandId] });
    },
  });

  return {
    tutorials,
    mainNavVideos,
    settingsVideos,
    isLoading,
    showTutorialIcons,
    isWatched,
    markAsWatched,
    clearWatchedTutorials,
    upsertTutorial,
    deleteTutorial,
  };
}

// Hook for admin page - fetches all tutorials including inactive
export function useMenuTutorialVideosAdmin() {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();

  const { data: tutorials = [], isLoading, refetch } = useQuery({
    queryKey: ['menu-tutorial-videos-admin', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      
      const { data, error } = await supabase
        .from('os_menu_tutorial_videos')
        .select(`
          *,
          lesson:os_lms_lessons(id, title, content, content_type)
        `)
        .eq('brand_id', currentBrandId)
        .order('menu_item_id');

      if (error) throw error;
      return data as MenuTutorialVideo[];
    },
    enabled: !!currentBrandId,
  });

  const upsertTutorial = useMutation({
    mutationFn: async (data: {
      menu_item_id: string;
      sidebar_type: 'main' | 'settings';
      lesson_id: string;
      is_active: boolean;
    }) => {
      if (!currentBrandId) throw new Error('No brand selected');
      
      const { error } = await supabase
        .from('os_menu_tutorial_videos')
        .upsert({
          brand_id: currentBrandId,
          menu_item_id: data.menu_item_id,
          sidebar_type: data.sidebar_type,
          lesson_id: data.lesson_id,
          is_active: data.is_active,
        }, {
          onConflict: 'brand_id,menu_item_id,sidebar_type',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos-admin', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos', currentBrandId] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('os_menu_tutorial_videos')
        .update({ is_active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos-admin', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos', currentBrandId] });
    },
  });

  const deleteTutorial = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_menu_tutorial_videos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos-admin', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['menu-tutorial-videos', currentBrandId] });
    },
  });

  return {
    tutorials,
    isLoading,
    refetch,
    upsertTutorial,
    toggleActive,
    deleteTutorial,
  };
}
